<template>
  <a-row class="input-container" :gutter="{ xs: 8, sm: 16 }">
    <a-col
      class="label-area"
      v-if="labelStatus"
      :xs="labelSpanData.xs"
      :sm="labelSpanData.sm"
      :md="labelSpanData.md"
      :lg="labelSpanData.lg"
      :xl="labelSpanData.xl"
      :xxl="labelSpanData.xxl"
    >
      <label :for="inputId">{{ label }}</label>
      <span v-if="requiredAreaStatus" class="ms-2 required-area hidden-sm-up">
        <span v-if="required" class="required-label">
          {{ $t('required') }}
        </span>
      </span>
    </a-col>
    <a-col
      :xs="inputSpanData.xs"
      :sm="inputSpanData.sm"
      :md="inputSpanData.md"
      :lg="inputSpanData.lg"
      :xl="inputSpanData.xl"
      :xxl="inputSpanData.xxl"
      :class="['input-area', inputAreaClass]"
    >
      <span
        v-if="requiredAreaStatus"
        :class="{
          'required-area hidden-sm-down': true,
          'visable-hidden': requiredAreaStatusVisableHidden
        }"
      >
        <span v-if="required" class="required-label">
          {{ $t('required') }}
        </span>
      </span>
      <span :class="{ 'input-main': true, 'no-note': note === '' }">
        <slot> </slot>
      </span>

      <span class="note-area" v-if="note !== ''">
        {{ note }}
      </span>
    </a-col>
  </a-row>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';

interface gridObject {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
}

var inputSpanData = ref<gridObject>({
  xs: 24,
  sm: 22,
  md: 22,
  lg: 22,
  xl: 22,
  xxl: 22
});

var labelSpanData = ref<gridObject>({
  xs: 24,
  sm: 2,
  md: 2,
  lg: 2,
  xl: 3,
  xxl: 3
});

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  labelStatus: {
    type: Boolean,
    default: true
  },
  note: {
    type: String,
    default: ''
  },
  inputId: {
    type: String,
    required: false
  },
  inputAreaClass: {
    type: String,
    default: ''
  },
  labelSpan: {
    type: [Object, Number],
    default: () => ({ xs: 24, sm: 6, md: 3, lg: 4, xl: 3, xxl: 2 })
  },
  inputSpan: {
    type: [Number, Object],
    default: 22
  },

  required: {
    type: Boolean,
    default: false
  },
  requiredAreaStatus: {
    type: Boolean,
    default: true
  },
  requiredAreaStatusVisableHidden: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  }
});

onMounted(() => {
  if (typeof props.inputSpan === 'number') {
    inputSpanData.value = {
      xs: props.inputSpan,
      sm: props.inputSpan,
      md: props.inputSpan,
      lg: props.inputSpan,
      xl: props.inputSpan,
      xxl: props.inputSpan
    };
  } else {
    inputSpanData.value = {
      xs: props.inputSpan.xs,
      sm: props.inputSpan.sm,
      md: props.inputSpan.md,
      lg: props.inputSpan.lg,
      xl: props.inputSpan.xl,
      xxl: props.inputSpan.xxl
    };
  }

  if (typeof props.labelSpan === 'number') {
    labelSpanData.value = {
      xs: props.labelSpan,
      sm: props.labelSpan,
      md: props.labelSpan,
      lg: props.labelSpan,
      xl: props.labelSpan,
      xxl: props.labelSpan
    };
  } else {
    labelSpanData.value = {
      xs: props.labelSpan.xs,
      sm: props.labelSpan.sm,
      md: props.labelSpan.md,
      lg: props.labelSpan.lg,
      xl: props.labelSpan.xl,
      xxl: props.labelSpan.xxl
    };
  }
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.input-container {
  .label-area {
  }

  label {
    display: block;
    line-height: 32px;
    width: fit-content;
    float: left;
  }
  .required-area {
    width: 40px;
    float: left;
    padding-top: 5px;
    .required-label {
      background-color: $red-color;
      color: white;
      padding: 3px 5px;
      border-radius: 3px;
      font-size: 11px;
    }
  }
  .input-area {
    display: inline;

    .input-main {
      width: calc(100% - 50px);
      position: relative;
      float: left;
      &.no-note {
        width: calc(100% - 40px);
      }
      @media (max-width: 576px) {
        width: 100% !important;
      }
    }
    &:has(.note-area) {
      .input-main {
        width: calc(100% - 70px);
      }
    }
    .note-area {
      width: 30px;
      padding-top: 5px;
      padding-left: 5px;
      float: left;
    }
    a-input[readonly] {
      background-color: #f5f5f5;
      color: #333;
      cursor: not-allowed;
    }
    input[readonly] {
      background-color: $gray-color !important;
    }
    .readonly-input {
      background-color: $gray-color;
    }
    @media (max-width: 576px) {
      display: flex;
      &:not(.no_reverse) {
        flex-direction: row-reverse;
        justify-content: flex-end;
      }
      .input-main {
        width: 100%;
      }
      &:has(.note-area) {
        .input-main {
          width: calc(100% - 20px);
        }
      }
    }
    @media (max-width: 576px) {
      .input-main {
        margin-bottom: -10px;
      }
    }
  }
}

.hidden-sm-up {
  @media (min-width: 576px) {
    display: none;
  }
}
.hidden-sm-down {
  @media (max-width: 576px) {
    display: none;
  }
}
.visable-hidden {
  visibility: hidden;
}
</style>
